import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import {
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import Tippy from '@tippyjs/react';
import styled from '../../../../../consts/styled';
import { colors, media, spacing } from '../../../../../App/ChakraTheme';
import { StudentTeam as IStudentTeam } from '../../../hooks/api/types';

import { usernameFullNameOrEmail } from '../../../../../helpers/username';
import { ChatbotStudent, GroupTemplate } from './api/studentData';
import { ProjectData } from './api/groupProject';
import Loader from '../../../../../components/Loader';
import { ConfirmBtn } from '../../../components/ConfirmBtn';
import { StudyGroupQuery } from '../../../../../generated/graphqlModels';
import { PlusIcon } from '../../../../../components/icons';
import { StemiInput } from '../../../../../components/Input';
import { useTranslation } from 'react-i18next';

const StudentTeamWrapper = styled.div`
  border-radius: 4px;
  transition: box-shadow 0.3s ease-out, transform 0.3s ease-out,
    background-color 0.3s ease-out;
  transition-delay: 0.1s;
  transform: translateY(0);
  box-sizing: border-box;

  .content {
    padding: ${spacing.BASE};
    ${media.phone`
  padding: ${spacing.SMALL};
`};
  }

  .label {
    color: ${colors.slategray};
    margin-bottom: 0px;
    min-width: 80px;
    width: 80px;
    margin-right: 10px;
  }

  h4 {
    margin-bottom: ${spacing.TINY};
  }
`;

const projectTypeOptions = [
  { label: 'RASA', value: 'RASA' },
  { label: 'ANSWERS', value: 'ANSWERS' },
];

interface StudentTeamProps {
  isProgramUsingFileServer: boolean;
  studentTeam: IStudentTeam;
  students: StudyGroupQuery['studyGroup']['students'];
  answersId: string;
  hasMissingStudent?: boolean;
  fileServerStudents?: ChatbotStudent[];
  groupTemplates?: GroupTemplate[];
  fileServerProject?: ProjectData;
  onNameChange: (name: string) => void;
  onTeamLeaderChange: (teamLeader: any) => void;
  onMemberChange: (member: any) => void;
  deleteTeam: () => void;
  createGroupProject: () => void;
  redirectToChatbotEditor: (id: string, name: string, type: string) => string;
  toggleProjectIsShown: () => Promise<void>;
  setAnswersId: (id: string) => void;
  save: () => Promise<boolean>;
}

const StudentTeam = (props: StudentTeamProps) => {
  const {
    isProgramUsingFileServer,
    hasMissingStudent,
    studentTeam,
    students,
    answersId,
    fileServerStudents,
    fileServerProject,
    groupTemplates,
    onNameChange,
    onTeamLeaderChange,
    onMemberChange,
    deleteTeam,
    createGroupProject,
    redirectToChatbotEditor,
    toggleProjectIsShown,
    setAnswersId,
    save,
  } = props;
  const [isEdit, setIsEdit] = useState(studentTeam.isEditMode ? true : false);
  const [isCreatingGroupProject, setIsCreatingGroupProject] = useState(false);
  const fileServerProjectType = fileServerProject?.type;
  const isFileServerProjectRasaOrAnswers =
    fileServerProjectType === 'RASA' || fileServerProjectType === 'ANSWERS';
  const [projectType, setProjectType] = useState(
    answersId ? 'ANSWERS' : 'RASA',
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (answersId) {
      setProjectType('ANSWERS');
    }
  }, [answersId]);

  const studentOptions = students.map((student) => ({
    label: usernameFullNameOrEmail(student as any),
    value: student.pk,
  }));

  const teamLeaderStudentOptions = studentOptions.filter((studentOption) =>
    studentTeam.members.find((member) => member.pk === studentOption.value),
  );

  const createGroupProjectHelper = async () => {
    setIsCreatingGroupProject(true);
    try {
      await createGroupProject();
    } catch (e) {
      console.log(e);
    }
    setIsCreatingGroupProject(false);
  };

  const studentTeamHasError =
    !studentTeam.isStudentTeamValid ||
    studentTeam.members.length === 0 ||
    !studentTeam.teamLeader;

  const errorMessage =
    studentTeam.teamLeader &&
    teamLeaderStudentOptions.findIndex(
      (option) => option.value === studentTeam.teamLeader!.pk,
    ) === -1
      ? t('class.studentTeam.teamLeadShouldBeTeamMember')
      : studentTeam.members.length === 0
      ? t('class.studentTeam.teamHasNoMembers')
      : !studentTeam.teamLeader
      ? t('class.studentTeam.teamHasNoLeader')
      : studentTeamHasError
      ? t('class.studentTeam.duplicateTeamName')
      : '';

  const toggleEditMode = (shouldDeleteIfEditMode = true) => {
    if (isEdit && shouldDeleteIfEditMode && studentTeam.isEditMode) {
      deleteTeam();
    }
    setIsEdit(!isEdit);
  };

  const closeWithoutSaving = () => {
    if (studentTeam.oldName) {
      onNameChange(studentTeam.oldName);
    }
    if (studentTeam.oldMembers) {
      const oldMembersOptions = studentTeam.oldMembers.map((m) => ({
        label: m.pk,
        value: m.pk,
      }));
      onMemberChange(oldMembersOptions);
    }
    toggleEditMode(true);
  };

  const onProjectTypeChange = ({ value }: any) => {
    setProjectType(value);
    if (value === 'RASA') {
      setAnswersId('');
    }
  };

  return (
    <>
      <Modal isOpen={isEdit} onClose={closeWithoutSaving} size="3xl">
        <ModalOverlay />
        <ModalContent background={studentTeamHasError ? '#ff9494' : '#ddd'}>
          <ModalHeader>
            <Text
              as="h4"
              textStyle="h4"
              style={{ textAlign: 'center', marginTop: 8 }}
            >
              {t('class.studentTeam.editStudentTeam')}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody zIndex={1}>
            <StudentTeamWrapper>
              <Text
                as="h5"
                textStyle="h5"
                style={{ color: '#FFF', textAlign: 'center' }}
              >
                {errorMessage}
              </Text>
              <HStack
                w="full"
                h="full"
                className="mobile-vertical content h-center"
              >
                <VStack w="full" align="center">
                  <HStack w="full" mb={3} align="center">
                    <Text as="label" textStyle="label">
                      {t('class.studentTeam.name')}
                    </Text>
                    <StemiInput
                      h="40px"
                      background="white"
                      placeholder={t('class.studentTeam.teamName')}
                      style={{ width: '100%', minWidth: '0px' }}
                      onChange={(event: any) =>
                        onNameChange(event.target.value)
                      }
                      defaultValue={studentTeam.name}
                    />
                  </HStack>
                  <HStack w="full" mb={3} align="center">
                    <Text as="label" textStyle="label">
                      {t('class.studentTeam.members')}
                    </Text>
                    <div style={{ display: 'flex', flex: 1 }}>
                      <Select
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: '100%',
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 2000,
                          }),
                        }}
                        isMulti
                        defaultValue={studentTeam.members.map((member) => ({
                          value: member.pk,
                          label: usernameFullNameOrEmail(member as any),
                        }))}
                        closeMenuOnSelect={false}
                        options={studentOptions}
                        onChange={onMemberChange}
                        placeholder={t('class.studentTeam.select')}
                      />
                    </div>
                  </HStack>
                  <HStack w="full" mb={3} align="center">
                    <Text as="label" textStyle="label">
                      {t('class.studentTeam.teamLeader')}
                    </Text>
                    <div style={{ display: 'flex', flex: 1 }}>
                      <Select
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: '100%',
                          }),
                        }}
                        defaultValue={
                          studentTeam.teamLeader && {
                            value: studentTeam.teamLeader.pk,
                            label: usernameFullNameOrEmail(
                              studentTeam.teamLeader as any,
                            ),
                          }
                        }
                        options={teamLeaderStudentOptions}
                        onChange={onTeamLeaderChange}
                        placeholder={t('class.studentTeam.select')}
                      />
                    </div>
                  </HStack>
                  {isProgramUsingFileServer &&
                    isFileServerProjectRasaOrAnswers &&
                    !studentTeamHasError &&
                    studentTeam.isSaved && (
                      <>
                        {fileServerProject ? (
                          <>
                            <Text
                              as="h4"
                              textStyle="h4"
                              style={{ textAlign: 'center', marginTop: '8px' }}
                            >
                              {t('class.studentTeam.groupProject')}
                            </Text>
                            <br />
                            <HStack w="full" mb={3} align="center">
                              <Text as="label" textStyle="label">
                                {t('class.studentTeam.projectType')}
                              </Text>
                              <Select
                                styles={{
                                  container: (base) => ({
                                    ...base,
                                    width: '100%',
                                  }),
                                }}
                                defaultValue={
                                  projectType === 'RASA'
                                    ? projectTypeOptions[0]
                                    : projectTypeOptions[1]
                                }
                                options={projectTypeOptions}
                                onChange={onProjectTypeChange}
                                placeholder={t('class.studentTeam.select')}
                              />
                            </HStack>
                            <HStack justify="space-evenly">
                              {false && (
                                <Button
                                  colorScheme={
                                    fileServerProject?.isShown
                                      ? 'tomato'
                                      : 'limegreen'
                                  }
                                  style={{
                                    maxWidth: '160px',
                                    backgroundColor: fileServerProject?.isShown
                                      ? colors.tomato
                                      : colors.limegreen,
                                  }}
                                  onClick={() => toggleProjectIsShown()}
                                >
                                  {fileServerProject?.isShown
                                    ? t('class.studentTeam.hidePublicProject')
                                    : t('class.studentTeam.makePublic')}
                                </Button>
                              )}
                              {projectType === 'ANSWERS' ? (
                                <Tippy
                                  arrow={true}
                                  theme="light"
                                  animation="scale"
                                  content={t(
                                    'class.studentTeam.leaveEmptyToKeepRASA',
                                  )}
                                >
                                  <div style={{ maxWidth: '160px' }}>
                                    <StemiInput
                                      background="white"
                                      placeholder={t(
                                        'class.studentTeam.answersId',
                                      )}
                                      onChange={(event: any) => {
                                        setAnswersId(event.target.value);
                                      }}
                                      defaultValue={answersId}
                                      style={{
                                        maxWidth: '160px',
                                        minWidth: '0px',
                                        textAlign: 'center',
                                        paddingLeft: 0,
                                      }}
                                    />
                                  </div>
                                </Tippy>
                              ) : null}
                            </HStack>
                          </>
                        ) : isCreatingGroupProject ? (
                          <Loader />
                        ) : (
                          <Button
                            disabled={isCreatingGroupProject}
                            colorScheme="blue"
                            type="button"
                            className="small"
                            leftIcon={<PlusIcon boxSize="16px" />}
                            onClick={createGroupProjectHelper}
                          >
                            {t('class.studentTeam.create')}
                          </Button>
                        )}
                      </>
                    )}
                </VStack>
              </HStack>
            </StudentTeamWrapper>
          </ModalBody>
          <ModalFooter zIndex={0}>
            <HStack w="full" justify="center" spacing={0} gap={4}>
              <ConfirmBtn
                variant="solid"
                colorScheme="red"
                message={t('class.studentTeam.areYouSure')}
                textColor="white"
                onConfirm={() => {
                  deleteTeam();
                  save();
                }}
              >
                {t('class.studentTeam.delete')}
              </ConfirmBtn>
              <ConfirmBtn
                variant="solid"
                colorScheme="blue"
                message={t('class.studentTeam.areYouSure')}
                textColor="white"
                onConfirm={async () => {
                  try {
                    const isSaved = await save();
                    if (isSaved) {
                      toggleEditMode(false);
                    }
                  } catch (e) {
                    console.log(e);
                  }
                }}
              >
                {t('class.studentTeam.save')}
              </ConfirmBtn>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <tr>
        <td>
          <Text as="h5" textStyle="h5" style={{ fontWeight: 'bold' }}>
            {studentTeam.name}
          </Text>
        </td>
        <td>
          {studentTeam.members
            .map((m) => usernameFullNameOrEmail(m as any))
            .join(', ')}
        </td>
        <td>
          {studentTeam.teamLeader
            ? usernameFullNameOrEmail(studentTeam.teamLeader as any)
            : t('class.studentTeam.noTeamLeader')}
        </td>
        {isProgramUsingFileServer && (
          <>
            <td>
              {fileServerProject ? (
                <div
                  style={{
                    fontWeight: 'bold',
                  }}
                >
                  {isFileServerProjectRasaOrAnswers
                    ? answersId
                      ? 'ANSWERS'
                      : 'RASA'
                    : fileServerProjectType}
                </div>
              ) : (
                t('class.studentTeam.noFinalProject')
              )}
            </td>
            <td>
              {fileServerProject ? (
                <Button
                  as={Link}
                  colorScheme="blue"
                  style={{ maxWidth: '160px' }}
                  to={redirectToChatbotEditor(
                    fileServerProject.id,
                    fileServerProject.name,
                    fileServerProject.type!,
                  )}
                  className="small"
                  target="_blank"
                >
                  {t('class.studentTeams.open')}
                </Button>
              ) : studentTeamHasError ? (
                t('class.studentTeam.teamHasError')
              ) : studentTeam.isSaved ? (
                isCreatingGroupProject ? (
                  <Loader />
                ) : hasMissingStudent ? (
                  <span>{t('class.studentTeam.notUsingEditor')}</span>
                ) : (
                  <Button
                    disabled={isCreatingGroupProject}
                    type="button"
                    className="small"
                    colorScheme="blue"
                    onClick={createGroupProjectHelper}
                  >
                    {t('class.studentTeam.create')}
                  </Button>
                )
              ) : (
                t('general.save')
              )}
            </td>
          </>
        )}
        <td>
          <Button
            className="small promoted"
            colorScheme="tomato"
            onClick={() => toggleEditMode()}
          >
            {t('class.studentTeam.edit')}
          </Button>
        </td>
      </tr>
    </>
  );
};

export default StudentTeam;
